import { ShareItemContainer, ShareMenu } from '@/contexts/shared/styles/ShareIconStyles'
import { FileCopyRounded, Share, WhatsApp } from '@mui/icons-material'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import ViewIcon from '@mui/icons-material/Visibility'
import { Alert, Box, Button, Divider, MenuItem, Snackbar, Typography } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { IRootState } from '@/redux/types'
import { ResourceListApi } from '@/contexts/backoffice/library/models/resource'
import CellTitleValue from '../../../../shared/components/CellTitleValue'
import VisibilityIcon from '../../../../shared/components/icons/VisibilityIcon'
import TableLanguagesCell from '../../../../shared/components/table/TableLanguagesCell'
import useGeneralSummaryReport from '../../../../shared/hooks/useGeneralSummaryReport'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'

function ResourceTable({ filters, nextPage }: any) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const navigate = useNavigate()

  const { resources } = useSelector((state: IRootState) => state.resource)

  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null)
  const [shareMenuOpen, setShareMenuOpen] = useState(false)
  const [shareAlert, setShareAlert] = useState(false)
  const [currentShareData, setCurrentShareData] = useState<any>(null)

  const { handleClickGeneralSummaryReport } = useGeneralSummaryReport(2)

  const handleDownload = (resource_id: number) => {
    handleClickGeneralSummaryReport(resource_id)
    const URL = `${import.meta.env.VITE_API_URL}/resources/download/`
    window.open(URL + resource_id)
  }

  const handleShareClick = (event: React.MouseEvent<HTMLElement>, rowData: any) => {
    setShareMenuAnchor(event.currentTarget)
    setShareMenuOpen(true)
    setCurrentShareData(rowData)
  }

  const handleShareMenuClose = () => {
    setShareMenuAnchor(null)
    setShareMenuOpen(false)
    setCurrentShareData(null)
  }

  const handleWhatsAppShare = () => {
    if (currentShareData) {
      const title = currentShareData.alias != null ? currentShareData.alias : currentShareData.file_name
      const link = `${window.location.origin}/biblioteca/${currentShareData.id}${window.location.search}`
      const text = encodeURIComponent(`${title} - ${link}`)
      window.open(`https://wa.me/?text=${text}`, '_blank')
    }
    handleShareMenuClose()
  }

  const handleCopyLink = async () => {
    if (currentShareData) {
      const link = `${window.location.origin}/biblioteca/${currentShareData.id}${window.location.search}`
      try {
        await navigator.clipboard.writeText(link)
        setShareAlert(true)
      } catch (_: any) {
        const textArea = document.createElement('textarea')
        textArea.value = link
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        setShareAlert(true)
      }
    }
    handleShareMenuClose()
  }

  const handleNativeShare = async () => {
    if (currentShareData && navigator.share) {
      const title = currentShareData.alias != null ? currentShareData.alias : currentShareData.file_name
      const link = `${window.location.origin}/biblioteca/${currentShareData.id}${window.location.search}`

      try {
        await navigator.share({
          title: title,
          text: currentShareData.description || title,
          url: link
        })
      } catch (err) {
        console.log('Error sharing:', err)
      }
    }
    handleShareMenuClose()
  }

  const initialState = {
    columns: {
      columnVisibilityModel: {
        date: !isMobile,
        language_codes: !isMobile,
        brand_name: filters.brand_id === null && !isMobile
      },
      pagination: {
        paginationModel: { pageSize: 10 }
      }
    }
  }

  const rows: GridRowsProp<ResourceListApi> = resources ?? []
  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('name'),
      flex: 1,
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center">
          <CellTitleValue value={params.value} />
          <Box ml={1}>
            <VisibilityIcon id={params.row.visibility} />
          </Box>
        </Box>
      )
    },
    {
      field: 'filetype',
      headerName: t('admin.library.format'),
      width: 180,
      renderCell: (params: any) => (
        <Button
          size="small"
          variant="outlined"
          sx={{
            borderColor: '#ff7c9c',
            color: '#ff7c9c',
            fontSize: '0.75rem',
            padding: '2px 10px',
            borderRadius: '6px',
            textTransform: 'none',
            backgroundColor: '#fff0f5',
            minWidth: 'auto',
            lineHeight: 1.4
          }}
        >
          {params.value}
        </Button>
      )
    },
    { field: 'language_codes', headerName: t('language'), width: 140, renderCell: (params: any) => <TableLanguagesCell params={params} /> },
    { field: 'date', headerName: t('upload-date'), width: 160 },
    { field: 'brand_name', headerName: t('brand'), width: 160 },
    {
      field: 'actions',
      type: 'actions',
      width: 30,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={`view-${params.id}`}
          icon={<ViewIcon />}
          label={t('view')}
          onClick={() => navigate(`/admin/biblioteca/${params.id}`)}
          showInMenu
        />,
        <GridActionsCellItem
          key={`download-${params.id}`}
          icon={<CloudDownloadIcon />}
          label={t('download')}
          onClick={() => handleDownload(Number(params.id))}
          showInMenu
        />,
        <GridActionsCellItem
          key={`share-${params.id}`}
          icon={<Share />}
          label={t('share')}
          onClick={event => handleShareClick(event, params.row)}
          showInMenu
        />
      ]
    }
  ]

  return (
    <Box
      sx={{
        paddingTop: isMobile ? 'auto' : '4rem',
        paddingBottom: '4rem',
        boxShadow: 'none',
        '& .MuiPaper-root': { background: '#fafafa' }
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        initialState={initialState}
        pageSizeOptions={[10, 20]}
        autoHeight
        sx={{
          border: 'none',
          fontSize: '0.88rem',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'transparent',
            fontWeight: 600,
            fontSize: '0.9rem',
            color: '#6e6e6e',
            borderBottom: '1px solid #ccc'
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #ccc',
            color: '#333'
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          }
        }}
      />
      <Box mt={6} pb={4} style={{ textAlign: 'center' }}>
        <Button variant="contained" size="large" color="primary" onClick={() => nextPage()}>
          {t('see-more')}
        </Button>
      </Box>

      <ShareMenu
        id="share-link-menu"
        anchorEl={shareMenuAnchor}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={shareMenuOpen}
        onClose={handleShareMenuClose}
      >
        <ShareItemContainer>
          {currentShareData?.visibility === 2 && (
            <MenuItem>
              <iframe
                title="facebook"
                src={`https://www.facebook.com/plugins/share_button.php?href=${encodeURIComponent(
                  `${window.location.origin}/biblioteca/${currentShareData?.id}${window.location.search}`
                )}&layout=button&size=small&appId=771862063439825&width=89&height=20`}
                width="89"
                height="20"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              />
            </MenuItem>
          )}

          <MenuItem onClick={handleWhatsAppShare}>
            <WhatsApp fontSize="small" />
            <Typography variant="caption">{t('share-on-whatsapp')}</Typography>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleCopyLink}>
            <FileCopyRounded fontSize="small" />
            <Typography variant="caption">{t('copy-link')}</Typography>
          </MenuItem>

          {navigator.share && (
            <>
              <Divider />
              <MenuItem onClick={handleNativeShare}>
                <Share fontSize="small" />
                <Typography variant="caption">{t('share')}</Typography>
              </MenuItem>
            </>
          )}
        </ShareItemContainer>
      </ShareMenu>

      <Snackbar
        open={shareAlert}
        autoHideDuration={3000}
        onClose={() => setShareAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">{t('text-copied-success')}</Alert>
      </Snackbar>
    </Box>
  )
}

export default ResourceTable
